import {createReducer, on} from '@ngrx/store';
import {
  ClearState,
  EditProfileFailure,
  EditProfileRequest,
  EditProfileSuccess,
  FetchHouseholdQuestionFailure,
  FetchHouseholdQuestionRequest,
  FetchHouseholdQuestionSuccess,
  FetchParticipantInformationFailure,
  FetchParticipantInformationRequest,
  FetchParticipantInformationSuccess,
  FetchParticipantsListFailure,
  FetchParticipantsListRequest,
  FetchParticipantsListSuccess,
  FetchPhiAndPiiQuestionFailure,
  FetchPhiAndPiiQuestionRequest,
  FetchPhiAndPiiQuestionSuccess,
  FetchRelationsFilterFailure,
  FetchRelationsFilterRequest,
  FetchRelationsFilterSuccess,
  FetchRelationshipQuestionFailure,
  FetchRelationshipQuestionRequest,
  FetchRelationshipQuestionSuccess,
  MarkHipaaConsentFailure,
  MarkHipaaConsentRequest,
  MarkHipaaConsentSuccess,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  SetLarStatus,
  UpdateRelationFailure,
  UpdateRelationRequest,
  UpdateRelationSuccess
} from './participants-list.actions';
import {ParticipantsModel} from "../../../../models/participants.model";
import {Question} from "../../../../models/question";
import {QuestionList} from "../../../../models/question-list";
import {Filter} from "../../../../models/filter";
import {ProfileModel} from "../../../../models/profile.model";

// State for this feature (User)
export interface ParticipantsListState {
  participantsList: {
    list: ParticipantsModel[],
    total: number,
    pageNumber: number,
    limit: number,
    hasNext: boolean,
    hasPrev: boolean,
    loading?: boolean
  },
  participantInformation: {
    info: ProfileModel,
    loading: boolean
  }
  relationshipQuestion: Question;
  householdQuestion: Question;
  phiAndPiiQuestionList: QuestionList;
  relationshipFilter: Filter,
  isLar: boolean,
  updateRelationInfo: {
    loading: boolean,
    relationStatusUpdated: boolean
  },
  saveAnswersInfo: {
    loading: boolean,
  },
  editProfileInfo: {
    loading: boolean
  },
  markHipaaConsentInfo: {
    loading: boolean
  }
}

const initialState: ParticipantsListState = {
  participantsList: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  participantInformation: {
    info: {
      firstName: '',
      lastName: '',
      emailId: '',
      gender: null,
      age: null,
      role: null,
      phoneNumber: '',
      username: '',
      isRegisterStepThreeCompleted: null,
      avatar: null,
      dob: '',
      hasAssent: null,
      hasMedication: null,
      hasMedicalIssue: null,
      medicationDescription: '',
      medicalDescription: null,
      height: null,
      weight: null,
      groupName: null,
      groupStart: null,
      groupStarted: null,
      participantStatus: null,
      bmi: null,
      hasConsent: null,
      hasHipaaConsent: null,
    },
    loading: false
  },
  relationshipQuestion: {
    options: [],
    id: null,
    question: '',
    dataType: null,
    loading: false
  },
  householdQuestion: {
    options: [],
    id: null,
    question: '',
    dataType: null,
    loading: false
  },
  phiAndPiiQuestionList: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  relationshipFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  isLar: false,
  updateRelationInfo: {
    loading: false,
    relationStatusUpdated: false
  },
  saveAnswersInfo: {
    loading: false,
  },
  editProfileInfo: {
    loading: false
  },
  markHipaaConsentInfo: {
    loading: false
  }
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, (state) => initialState),

  // FETCH PARTICIPANTS LIST
  on(FetchParticipantsListRequest, (state) => ({
    ...state,
    participantsList: {
      ...initialState.participantsList,
      loading: true
    }
  })),
  on(FetchParticipantsListSuccess, (state, props) => ({
    ...state,
    participantsList: {
      ...props.participantsList,
      loading: false
    }
  })),
  on(FetchParticipantsListFailure, (state) => ({
    ...state,
    participantsList: {
      ...initialState.participantsList,
      loading: false
    }
  })),

  // FETCH  PARTICIPANT INFORMATION
  on(FetchParticipantInformationRequest, (state) => ({
    ...state,
    participantInformation: {
      info: initialState.participantInformation.info,
      loading: true
    }
  })),
  on(FetchParticipantInformationSuccess, (state, props) => ({
    ...state,
    participantInformation: {
      info: props.participantInformation,
      loading: false
    }
  })),
  on(FetchParticipantInformationFailure, (state) => ({
    ...state,
    participantInformation: {
      info: initialState.participantInformation.info,
      loading: false
    }
  })),

  // FETCH RELATIONSHIP QUESTIONS
  on(FetchRelationshipQuestionRequest, (state) => ({
    ...state,
    relationshipQuestion: {
      ...initialState.relationshipQuestion,
      loading: true
    }
  })),
  on(FetchRelationshipQuestionSuccess, (state, props) => ({
    ...state,
    relationshipQuestion: {
      ...props.relationshipQuestion,
      loading: false
    }
  })),
  on(FetchRelationshipQuestionFailure, (state) => ({
    ...state,
    relationshipQuestion: {
      ...initialState.relationshipQuestion,
      loading: false
    }
  })),

  // FETCH HOUSEHOLD QUESTIONS
  on(FetchHouseholdQuestionRequest, (state) => ({
    ...state,
    householdQuestion: {
      ...initialState.householdQuestion,
      loading: true
    }
  })),
  on(FetchHouseholdQuestionSuccess, (state, props) => ({
    ...state,
    householdQuestion: {
      ...props.householdQuestion,
      loading: false
    }
  })),
  on(FetchHouseholdQuestionFailure, (state) => ({
    ...state,
    householdQuestion: {
      ...initialState.householdQuestion,
      loading: false
    }
  })),


  // FETCH PHI AND PII QUESTIONS
  on(FetchPhiAndPiiQuestionRequest, (state) => ({
    ...state,
    phiAndPiiQuestionList: {
      ...initialState.phiAndPiiQuestionList,
      loading: true
    }
  })),
  on(FetchPhiAndPiiQuestionSuccess, (state, props) => ({
    ...state,
    phiAndPiiQuestionList: {
      ...props.phiAndPiiQuestionList,
      loading: false
    }
  })),
  on(FetchPhiAndPiiQuestionFailure, (state) => ({
    ...state,
    phiAndPiiQuestionList: {
      ...initialState.phiAndPiiQuestionList,
      loading: false
    }
  })),

  // FETCH RELATIONS FILTER
  on(FetchRelationsFilterRequest, (state) => ({
    ...state,
    relationshipFilter: {
      ...initialState.relationshipFilter,
      loading: true
    }
  })),
  on(FetchRelationsFilterSuccess, (state, props) => ({
    ...state,
    relationshipFilter: {
      ...props.relationshipFilter,
      loading: false
    }
  })),
  on(FetchRelationsFilterFailure, (state) => ({
    ...state,
    relationshipFilter: {
      ...state.relationshipFilter,
      loading: false
    }
  })),

  // SET LAR STATUS
  on(SetLarStatus, (state, props) => ({
    ...state,
    isLar: props.isLar
  })),


  // UPDATE RELATION
  on(UpdateRelationRequest, (state) => ({
    ...state,
    updateRelationInfo: {
      loading: true,
      relationStatusUpdated: false
    }
  })),
  on(UpdateRelationSuccess, (state) => ({
    ...state,
    updateRelationInfo: {
      loading: false,
      relationStatusUpdated: true,
    }
  })),
  on(UpdateRelationFailure, (state) => ({
    ...state,
    updateRelationInfo: {
      loading: false,
      relationStatusUpdated: false
    }
  })),

  // SAVE ANSWERS
  on(SaveAnswersRequest, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: true,
    }
  })),
  on(SaveAnswersSuccess, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: false,
    }
  })),
  on(SaveAnswersFailure, (state) => ({
    ...state,
    saveAnswersInfo: {
      loading: false,
    }
  })),

  // EDIT PROFILE
  on(EditProfileRequest, (state) => ({
    ...state,
    editProfileInfo: {
      loading: true,
    }
  })),
  on(EditProfileSuccess, (state) => ({
    ...state,
    editProfileInfo: {
      loading: false,
    }
  })),
  on(EditProfileFailure, (state) => ({
    ...state,
    editProfileInfo: {
      loading: false
    }
  })),

  // MARK HIPAA CONSENT
  on(MarkHipaaConsentRequest, (state) => ({
    ...state,
    markHipaaConsentInfo: {
      loading: true,
    }
  })),
  on(MarkHipaaConsentSuccess, (state) => ({
    ...state,
    markHipaaConsentInfo: {
      loading: false,
    }
  })),
  on(MarkHipaaConsentFailure, (state) => ({
    ...state,
    markHipaaConsentInfo: {
      loading: false
    }
  })),
);
